// src/components/SpacyScanner.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Two exported components:
//    <SpacyThinkingBubble />  →  replaces the isSpacyThinking block in the chat
//    <ScanBorderWrapper />    →  wraps the conversation container
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import { Bot } from 'lucide-react';

// 🔥 AUDIO INYECTADO: Importamos el sonido desde assets
import scannerAudioFile from '../assets/scanner-alert.mp3';

// ── Message Pools ─────────────────────────────────────────────────────────────

const TUTOR_MESSAGES = [
    { icon: '🎙️', text: 'Analyzing Phonological Control...' },
    { icon: '🧠', text: 'Mapping Lexical Resource...' },
    { icon: '📐', text: 'Validating Grammatical Range...' },
    { icon: '🌊', text: 'Measuring Discourse Coherence...' },
    { icon: '🎯', text: 'Calibrating CEFR Level Alignment...' },
    { icon: '⚡', text: 'Processing Semantic Accuracy...' },
    { icon: '🔬', text: 'Evaluating Pragmatic Competence...' },
];

const FREE_MESSAGES = [
    { icon: '🎭', text: 'Reading the room...' },
    { icon: '🔥', text: 'Calibrating character intensity...' },
    { icon: '⚡', text: 'Processing your response...' },
    { icon: '🌀', text: 'Improvising scenario dynamics...' },
    { icon: '🎪', text: 'Crafting the perfect reply...' },
    { icon: '🧩', text: 'Connecting contextual patterns...' },
    { icon: '💡', text: 'Generating adaptive feedback...' },
];

// ── Thinking Bubble ───────────────────────────────────────────────────────────

// `mensajes` es opcional y NO cambia nada de lo que ya habia: si no se pasa,
// se siguen usando las listas de arriba, que son las de la leccion. Existe
// porque Spacy Coach no analiza la fonologia de nadie — consulta el expediente,
// las clases y el examen— y ademas su pantalla cambia de idioma con el alumno.
// Un mismo motor de espera, dos repertorios.
export function SpacyThinkingBubble({ isActive, mode = 'tutor', avatarIcon = null,
                                      mensajes = null }) {
    const [showScan, setShowScan] = useState(false);
    const [msgIndex, setMsgIndex] = useState(0);
    const [phase, setPhase] = useState('in');

    // 🔥 REFERENCIA DE AUDIO: Mantiene el motor de sonido vivo sin re-renderizar
    const audioRef = useRef(null);

    const messages = (Array.isArray(mensajes) && mensajes.length > 0)
        ? mensajes
        : (mode === 'free' ? FREE_MESSAGES : TUTOR_MESSAGES);
    const msg = messages[msgIndex];

    // 🔥 CIRUGÍA MAESTRA: Escáner Inmediato + Motor de Audio a prueba de balas
    useEffect(() => {
        // 1. Instanciamos el audio
        if (!audioRef.current) {
            audioRef.current = new Audio(scannerAudioFile);
            audioRef.current.loop = true;
            audioRef.current.volume = 0.2;
        }

        // 2. Le damos Play
        audioRef.current.play().catch(err => console.log("Audio esperando...", err));
        setShowScan(true);

        // 3. 🛑 EL SEGURO DE VIDA: Función de Limpieza
        // React ejecutará esto automáticamente justo antes de destruir el componente
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
        };
    }, []); // <-- El array vacío asegura que esto se ejecuta solo al nacer y al morir

    // Cycle through messages with a fade-out → swap → fade-in transition
    useEffect(() => {
        if (!showScan) return;
        const interval = setInterval(() => {
            setPhase('out');
            setTimeout(() => {
                setMsgIndex(prev => (prev + 1) % messages.length);
                setPhase('in');
            }, 320);
        }, 2600);
        return () => clearInterval(interval);
    }, [showScan, messages.length]);

    return (
        <div className="ssc-thinking-wrapper">
            <div className="ssc-avatar-icon">
                {avatarIcon || <Bot size={20} />}
            </div>
            <div className="ssc-bubble-stack">

                {/* Three bouncing dots — always visible while thinking */}
                <div className="ssc-dots-bubble">
                    <span /><span /><span />
                </div>

                {/* Scanning analysis card — appears immediately now */}
                {showScan && (
                    <div className={`ssc-scan-card ssc-phase-${phase}`} key={msgIndex}>
                        <div className="ssc-scan-row">
                            <span className="ssc-scan-icon">{msg.icon}</span>
                            <span className="ssc-scan-text">{msg.text}</span>
                        </div>
                        {/* Progress bar that fills during each message's lifespan */}
                        <div className="ssc-scan-bar">
                            <div className="ssc-scan-fill" />
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}

// ── Scan Border Wrapper ───────────────────────────────────────────────────────
//
//  Wraps any container. When isActive=true, an animated light beam travels
//  clockwise around the border of the wrapped element.
//
//  How it works (no overflow:hidden on the content needed):
//    1. .ssc-ring  sits at inset:-3px (behind content, z-index:0, overflow:hidden)
//    2. .ssc-ring-beam  is a 200×200% spinning conic-gradient clipped by .ssc-ring
//    3. Child content sits at z-index:1 — its background covers the ring's center
//    4. Only the 3px border gap shows the gradient → animated border effect ✓

export function ScanBorderWrapper({ isActive, children }) {
    return (
        <div className={`ssc-border-wrapper${isActive ? ' ssc-scanning' : ''}`}>
            <div className="ssc-ring" aria-hidden="true">
                <div className="ssc-ring-beam" />
            </div>
            {children}
        </div>
    );
}